import styled from 'styled-components';
import { FlexSpacer } from 'ui/flexSpacer';
import { Progress } from 'ui/progress';
import { ProgressVariants } from 'ui/progress/Progress.types';
import { Body2Medium, Caption, colors, fontWeights, heading3 } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { localizeCompactNumber, localizeNumber, localizePercent } from 'utils/l10n';
import { isDefined } from 'utils/tsHelpers';

const Wrapper = styled(FlexSpacer)`
  flex: 1 0 0;
`;

const Row = styled(FlexSpacer)`
  width: 100%;  
`;

const MetricValue = styled(Body2Medium)`
  ${heading3}
  margin-top: 0;
  margin-bottom: 0;
`;

const DetailCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

const DetailValue = styled(Caption)`
  color: ${colors.textSecondary};
  font-weight: ${fontWeights.bold};
`;

export type ProgressIndicatorProps = {
  isValueFirst?: boolean;
  limit: number;
  timePeriod?: string;
  value: number;
};

export const ProgressIndicator = ({
  isValueFirst,
  limit,
  timePeriod = 'maximum',
  value,
}: ProgressIndicatorProps) => {
  const progressValue = (value / limit) * 100;
  const percent = localizePercent(value / limit, 2);

  return (
    <>
      <Row
        data-test="metric-progress-bar"
        flexDirection={isValueFirst ? 'row-reverse' : 'row'}
        justifyContent="space-between"
      >
        <DetailCaption>{`/${localizeCompactNumber(limit)} ${timePeriod}`}</DetailCaption>
        <DetailValue>{percent}</DetailValue>
      </Row>
      <Progress
        value={progressValue}
        variant={progressValue < 100 ? ProgressVariants.info : ProgressVariants.error}
        max={100}
      />
    </>
  );
};

export type AnalyticTileProps = {
  /** Add tooltip descriptiion to metric name */
  metricDescription?: string;
  /** Name of the metric shown */
  metricName: string;
  /** Current metric value to display */
  metricValue: number;
  /** Additional context for current metric */
  metricValuePrefix?: string;
  /** Total of metric over a time period */
  totalMetricValue?: number;
  /** Additional context for total metric */
  totalMetricValuePrefix?: string;
  /** time period used as stand alone date(s) or reference to limit value */
  timePeriod?: string;
  /** Limit as reference point */
  limit?: number;
};

export const AnalyticTile = ({
  metricDescription = '',
  metricName,
  metricValue,
  metricValuePrefix,
  totalMetricValue,
  totalMetricValuePrefix,
  timePeriod,
  limit,
}: AnalyticTileProps) => {
  return (
    <Wrapper flexDirection="column" alignItems="flex-start" gap="half">
      <div>
        <Tooltip isEnabled={!!metricDescription} isUnderlined tooltipContent={metricDescription}>
          {metricName}
        </Tooltip>
      </div>
      <Row gap="quarter" justifyContent="space-between">
        {metricValuePrefix && <DetailCaption>{metricValuePrefix}</DetailCaption>}
        <MetricValue>{localizeNumber(metricValue)}</MetricValue>
      </Row>
      {isDefined(totalMetricValue) && (
        <Row data-test="total-metric-value" justifyContent="space-between">
          {totalMetricValuePrefix && <DetailCaption>{totalMetricValuePrefix}</DetailCaption>}
          <DetailValue>{totalMetricValue.toLocaleString()}</DetailValue>
        </Row>
      )}
      {Boolean(timePeriod && !limit) && (
        <Row data-test="metric-time-period">
          <DetailCaption>{timePeriod}</DetailCaption>
        </Row>
      )}
      {isDefined(limit) && (
        <ProgressIndicator
          isValueFirst={!!metricValuePrefix}
          limit={limit}
          value={totalMetricValue ?? metricValue}
          timePeriod={timePeriod}
        />
      )}
    </Wrapper>
  );
};
